import type { Time } from "@suse-o11y/date-time/time";
import { DialogBoundary, DialogTrigger } from "@suse-o11y/dialog";
import { IconDashboard } from "@suse-o11y/icons";
import { type Loading, RemoteData, type Success } from "@suse-o11y/remote-data";
import { Template } from "@suse-o11y/template";
import React from "react";

import type { MetricQuery } from "~/models/metric-query";

import { normalizePinnedMetricComponent } from "../../bottom_content/view/panes/east/pinned-items/PinnedItem";
import { DashboardChartPluginSpec, DashboardWidgetSpec } from "../../common/dashboard-spec";
import { TimeSeriesQuery } from "../../common/dashboard-spec/_time-series-query";
import { generatePath } from "../../generic/routing";
import { queryFullComponent } from "../../queries/components";
import { useQuery } from "../../queries/react-query";
import { COMPONENT_PATH } from "../../routes/Routes";
import { AddToDashboardDrawer } from "../_add-to-dashboard-drawer";
import { fromMetricQuery as widgetQueryFromMetricQuery } from "../widget-form/query/spec";
import type { Spec as ChartSpec } from "../widget-form/spec";

interface Model {
	title: undefined | string;
	componentIdentifier: undefined | string;
	timestamp: undefined | Time;
	metricQueries: ReadonlyArray<MetricQuery>;
	chart: ChartSpec;
}

export interface AddToDashboardButtonProps {
	model: Loading | Success<Model>;
}

export const AddToDashboardButton: React.FC<AddToDashboardButtonProps> = ({ model }) => {
	const queryParams = model.getOrElse(undefined);

	const { data: component, isLoading: isComponentLoading } = useQuery({
		enabled: queryParams?.componentIdentifier != null,
		refetchOnMount: false,
		refetchOnReconnect: false,
		refetchOnWindowFocus: false,
		select: normalizePinnedMetricComponent,

		...queryFullComponent({
			componentIdOrIdentifier: queryParams?.componentIdentifier ?? "",
			timestamp: queryParams?.timestamp,
		}),
	});

	const widget = React.useMemo(() => {
		if (isComponentLoading) {
			return RemoteData.loading();
		}

		return model.transform(({ title, metricQueries, chart }) =>
			DashboardWidgetSpec.of({
				name: Template(title ?? ""),
				plugin: {
					links: component
						? [
								{
									url: Template(`/#${generatePath(COMPONENT_PATH, { identifier: component.identifier })}`),
									name: Template(`${component.name} (${component.type})`),
									targetBlank: false,
								},
							]
						: [],

					queries: metricQueries.map((query) => TimeSeriesQuery.fromQuerySpec(widgetQueryFromMetricQuery(query))),
					...DashboardChartPluginSpec.fromChartSpec(chart),
				},
			}),
		);
	}, [isComponentLoading, component, model]);

	return (
		<DialogBoundary>
			<DialogTrigger className="button button-tertiary disabled:skeleton gap-2" disabled={!widget.isSuccess()}>
				<IconDashboard className="text-15" />
				Add to dashboard
			</DialogTrigger>

			{widget.isSuccess() && <AddToDashboardDrawer widgets={[widget.value]} />}
		</DialogBoundary>
	);
};
